/**
 * BOM rebuild modal. Lets the operator change a Pending Megaventory work order's BOM by:
 *   - Editing existing component lines in place (replacement SKU and quantity).
 *   - Adding new lines.
 *   - Omitting lines from the rebuilt BOM by setting replacement quantity to 0 (rows stay in the table so the
 *     operator can see what was zeroed; Megaventory still receives only non-zero lines).
 *
 * Confirming calls the hub immediately: Megaventory's multi-step path (temporarily edit product BOM, clone work
 * order, delete original, refresh Supabase, revert product BOM) runs in one request.
 */

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Plus, X } from "lucide-react";

import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label } from "@/components/ui/Input";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";

interface MvProductBomMaterial {
  ProductSKU: string;
  ProductDescription: string | null;
  RawMaterialQuantity: number;
  ProductID: number | null;
}

interface MvProductBom {
  ProductSKU: string;
  mvRawMaterials: MvProductBomMaterial[];
}

interface WorkOrderDetailMinimal {
  id: number;
  number: string;
  mv_id: number | null;
  mv_number: string | null;
  mv_status: string | null;
  sku: string | null;
}

interface RebuildLine {
  productSku: string;
  rawMaterialQuantity: number;
  replacementSku: string | null;
  replacementQuantity: number | null;
}

interface DialogRow {
  /**
   * Stable id for the React key — works when adding new rows that are not yet bound to a Megaventory
   * ProductID. Falls back to a synthetic id for added rows.
   */
  rowKey: string;
  productSku: string;
  rawMaterialQuantity: number;
  replacementSku: string;
  replacementQuantity: string;
  productDescription: string | null;
}

function rowKeyFor(material: MvProductBomMaterial, index: number): string {
  if (material.ProductID != null && Number.isFinite(material.ProductID)) {
    return `mv-${material.ProductID}`;
  }
  return `idx-${index}-${material.ProductSKU}`;
}

function fetchProductBom(productSku: string): Promise<{ bom: MvProductBom | null }> {
  return apiFetch<{ bom: MvProductBom | null }>("/api/manufacturing/product-bom", {
    query: { sku: productSku },
  });
}

export function RebuildBomDialog({
  workOrder,
  onClose,
  onConfirm,
  isSubmitting,
  error,
  successMessage,
  hubWritesAllowed = true,
}: {
  workOrder: WorkOrderDetailMinimal;
  onClose: () => void;
  onConfirm: (lines: RebuildLine[]) => void;
  isSubmitting: boolean;
  error: ApiError | null;
  successMessage: string | null;
  hubWritesAllowed?: boolean;
}) {
  const sku = workOrder.sku?.trim() ?? "";
  const productBomQuery = useQuery<{ bom: MvProductBom | null }, ApiError>({
    queryKey: ["manufacturing", "product-bom", sku],
    queryFn: () => fetchProductBom(sku),
    enabled: sku !== "",
    staleTime: 60_000,
  });

  const [rows, setRows] = useState<DialogRow[]>([]);

  useEffect(() => {
    const materials = productBomQuery.data?.bom?.mvRawMaterials ?? [];
    setRows(
      materials
        .filter((material) => material.ProductSKU != null && material.ProductSKU.trim() !== "")
        .map((material, index) => ({
          rowKey: rowKeyFor(material, index),
          productSku: material.ProductSKU,
          rawMaterialQuantity: Number(material.RawMaterialQuantity ?? 0),
          replacementSku: "",
          replacementQuantity: "",
          productDescription: material.ProductDescription,
        })),
    );
  }, [productBomQuery.data?.bom?.ProductSKU, productBomQuery.data]);

  const tempBomPreview = useMemo(() => {
    return rows
      .map((row) => {
        const sku = row.replacementSku.trim() !== "" ? row.replacementSku.trim() : row.productSku;
        const quantity =
          row.replacementQuantity.trim() !== "" ? Number(row.replacementQuantity) : row.rawMaterialQuantity;
        return { sku, quantity };
      })
      .filter((line) => line.sku !== "" && Number.isFinite(line.quantity) && line.quantity > 0);
  }, [rows]);

  function setRow(rowKey: string, patch: Partial<DialogRow>): void {
    setRows((current) => current.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)));
  }

  function addRow(): void {
    if (!hubWritesAllowed) return;
    const id = `new-${Date.now()}-${rows.length}`;
    setRows((current) => [
      ...current,
      {
        rowKey: id,
        productSku: "",
        rawMaterialQuantity: 0,
        replacementSku: "",
        replacementQuantity: "",
        productDescription: null,
      },
    ]);
  }

  function buildLines(): RebuildLine[] {
    return rows
      .map((row) => ({
        productSku: row.productSku,
        rawMaterialQuantity: row.rawMaterialQuantity,
        replacementSku: row.replacementSku.trim() === "" ? null : row.replacementSku.trim(),
        replacementQuantity:
          row.replacementQuantity.trim() === "" ? null : Number(row.replacementQuantity),
      }))
      .filter((line) => line.productSku !== "" || (line.replacementSku ?? "") !== "");
  }

  const status = (workOrder.mv_status ?? "").trim().toLowerCase();
  const blocked = status !== "pending";

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true">
      <Card className="my-4 w-full max-w-3xl shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Rebuild BOM — {workOrder.mv_number}</CardTitle>
            <button
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close rebuild BOM"
              type="button"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <div>
                <div className="font-semibold">This action updates Megaventory immediately.</div>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                  <li>
                    The server clones into a new Megaventory work order, deletes the original, and reverts the finished
                    good product BOM.
                  </li>
                  <li>Allowed only when the work order is Pending in Megaventory.</li>
                  <li>
                    Supabase <code className="rounded bg-amber-100 px-1">build_order.mv_id</code> and{" "}
                    <code className="rounded bg-amber-100 px-1">mv_number</code> are rewritten so they cannot drift.
                  </li>
                  <li>
                    Draft rebuilds from the manufacturing connector stay in the connector rebuild queue until someone
                    approves them there — this dialog is for hub operators only.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {blocked ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Megaventory work order status is <strong>{workOrder.mv_status ?? "unknown"}</strong>. Only Pending
              orders can be rebuilt.
            </div>
          ) : null}

          {productBomQuery.isLoading ? (
            <div className="text-sm text-slate-500">Loading current product BOM from Megaventory…</div>
          ) : productBomQuery.error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {productBomQuery.error.message}
            </div>
          ) : productBomQuery.data?.bom == null ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Megaventory has no product BOM for {sku}. The page cannot revert at the end of the rebuild, so this
              flow is disabled.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Edit BOM lines</Label>
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint="Your role has read-only access to the Manufacturing hub module."
                  layout="inline"
                >
                  <Button variant="ghost" size="sm" onClick={addRow} disabled={!hubWritesAllowed}>
                    <Plus className="h-4 w-4" aria-hidden /> Add line
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Original SKU</th>
                      <th className="px-2 py-1.5 text-right">Original qty</th>
                      <th className="px-2 py-1.5 text-left">Replacement SKU</th>
                      <th className="px-2 py-1.5 text-right">Replacement qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((row) => {
                      const replacementZeroed =
                        row.replacementQuantity.trim() !== "" &&
                        Number.isFinite(Number(row.replacementQuantity)) &&
                        Number(row.replacementQuantity) === 0;
                      return (
                      <tr
                        key={row.rowKey}
                        className={replacementZeroed ? "bg-slate-50 text-slate-600" : undefined}
                      >
                        <td className="px-2 py-1.5 align-middle">
                          <span className="block min-h-8 px-2 py-1.5 font-mono text-xs text-slate-900">
                            {row.productSku !== "" ? row.productSku : "—"}
                          </span>
                          {row.productDescription ? (
                            <span className="block px-2 text-[11px] leading-snug text-slate-500">
                              {row.productDescription}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5 align-middle text-right tabular-nums">
                          <span className="inline-block min-h-8 px-2 py-1.5 font-mono text-xs text-slate-900">
                            {row.rawMaterialQuantity}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <AssemblySkuCombobox
                            value={row.replacementSku}
                            onChange={(next) => setRow(row.rowKey, { replacementSku: next })}
                            placeholder="leave empty to keep original"
                            disabled={!hubWritesAllowed}
                          />
                        </td>
                        <td className="px-2 py-1.5 align-top text-right tabular-nums">
                          <Input
                            className="h-8 text-right font-mono text-xs"
                            type="number"
                            min="0"
                            step="0.0001"
                            value={row.replacementQuantity}
                            disabled={!hubWritesAllowed}
                            onChange={(event) => {
                              const raw = event.target.value;
                              if (raw.trim() === "") {
                                setRow(row.rowKey, { replacementQuantity: "" });
                                return;
                              }
                              const parsed = Number(raw);
                              if (Number.isFinite(parsed) && parsed === 0) {
                                setRow(row.rowKey, { replacementQuantity: "0" });
                                return;
                              }
                              setRow(row.rowKey, { replacementQuantity: raw });
                            }}
                            placeholder={String(row.rawMaterialQuantity)}
                          />
                        </td>
                      </tr>
                      );
                    })}
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-500">
                          No BOM lines.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> {error.message}
            </div>
          ) : null}
          {successMessage ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              {successMessage}
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="Your role has read-only access to the Manufacturing hub module."
                layout="inline"
              >
                <Button
                  onClick={() => onConfirm(buildLines())}
                  isLoading={isSubmitting}
                  disabled={
                    !hubWritesAllowed ||
                    blocked ||
                    tempBomPreview.length === 0 ||
                    productBomQuery.data?.bom == null
                  }
                >
                  Rebuild BOM
                </Button>
              </ReadOnlyHoverLayer>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
